import React, { useMemo } from 'react';
import { isEmpty } from 'lodash';
import classnames from 'classnames';
import { Divider, Grid, Stack } from '@fiverr-private/layout_components';
import { Typography } from '@fiverr-private/typography';
import { I18n, translate } from '@fiverr-private/i18n-react';
import { useBriefContext } from '@fiverr-private/ai_brief/dist/esm/src/common/BriefContext';
import { T_LANGUAGE_PREFIX } from '../utils/consts';
import { IdealCandidatePreferences } from '../utils/types';
import { getCountriesAndRegionsText, getFreelancerTypeText, getLanguageName, getTimezoneText } from '../utils/utils';
import { ViewComponentProps } from '../..';
import { AppI18n } from '../../../../i18n';
import { collapseOnHover, collapsible } from '../styles.ve.css';

const getLanguageMinLevelText = (minLevel: string) => translate(`${T_LANGUAGE_PREFIX}.levels.${minLevel}`);
const getLanguageLevelText = (language: string, level: string) =>
  `${getLanguageName(language)}: ${getLanguageMinLevelText(level)}`;

const Section = ({ children, sectionName }: { children: React.ReactNode; sectionName: string }) => (
  <Stack direction="column" fontSize="b_md" color="grey_1200" gap="1">
    <Typography size="b_sm" color="bodySecondary">
      <I18n k={`dm_brief_creation.sections.ideal_candidate.${sectionName}.title`} />:
    </Typography>
    <Stack>{children}</Stack>
  </Stack>
);

export const IdealCandidateView = (props: ViewComponentProps) => {
  const { section } = props;
  const { editedSectionId } = useBriefContext();
  const {
    languages = [],
    countries = [],
    regions = [],
    timezone,
    freelancerType,
    skillsAndExperience,
  } = (section.content as IdealCandidatePreferences) ?? {};

  const allChosen = useMemo(
    () =>
      !isEmpty(languages) &&
      (!isEmpty(countries) || !isEmpty(regions) || timezone?.timezoneId) &&
      skillsAndExperience &&
      !isEmpty(freelancerType),
    [languages, countries, regions, timezone, freelancerType, skillsAndExperience]
  );

  const isSomeChosen = useMemo(
    () =>
      !isEmpty(languages) ||
      !isEmpty(countries) ||
      !isEmpty(regions) ||
      !isEmpty(timezone) ||
      !isEmpty(freelancerType) ||
      !isEmpty(skillsAndExperience),
    [languages, countries, regions, timezone, freelancerType, skillsAndExperience]
  );

  const locations = getCountriesAndRegionsText({ countries, regions });

  return (
    <Stack direction="column" gap="0" width="100%">
      <Stack direction="column" gap="3" width="100%">
        {skillsAndExperience && (
          <Section sectionName="skills_and_experience">
            <Typography as="pre" whiteSpace="preWrap" overflowWrap="anywhere">
              {skillsAndExperience}
            </Typography>
          </Section>
        )}
        {languages.length > 0 && (
          <Section sectionName="languages">
            <Stack wrap={{ default: 'wrap' }} flexShrink={1}>
              {languages.map((language, index) => (
                <Stack key={index} direction="row" display="inlineFlex" gap="1">
                  <Typography color="grey_1200" as="p" size="b_md">
                    {getLanguageLevelText(language.languageCode, language.minProficiency.toLowerCase())}
                  </Typography>
                  {index !== languages.length - 1 && (
                    <Divider hidden={{ default: true, sm: false }} height="20px" marginX={'2'} direction="vertical" />
                  )}
                </Stack>
              ))}
            </Stack>
          </Section>
        )}
        {locations.length > 0 && (
          <Section sectionName="location_timezone.location">
            <Typography>{locations.join(', ')}</Typography>
          </Section>
        )}
        {timezone?.timezoneId && (
          <Section sectionName="location_timezone.timezone">
            <Typography>{getTimezoneText(timezone.timezoneId, timezone.maxOffsetHours)}</Typography>
          </Section>
        )}
        {freelancerType?.minLevel && (
          <Section sectionName="type_level.level">
            <Typography>{getFreelancerTypeText(freelancerType.minLevel.toLocaleLowerCase())}</Typography>
          </Section>
        )}
        {(freelancerType?.isVettedPro || freelancerType?.agencyPreference) && (
          <Section sectionName="type_level.type">
            {freelancerType.isVettedPro && <Typography>{getFreelancerTypeText('prioritize_vetted_pro')}</Typography>}
            {freelancerType.isVettedPro && freelancerType.agencyPreference && (
              <Divider hidden={{ default: true, sm: false }} height="20px" marginX={'2'} direction="vertical" />
            )}
            {freelancerType.agencyPreference && (
              <Typography>{getFreelancerTypeText(freelancerType.agencyPreference.toLocaleLowerCase())}</Typography>
            )}
          </Section>
        )}
      </Stack>
      <Grid
        gap="0"
        width="100%"
        overflow="hidden"
        minHeight={0}
        className={classnames(collapsible, {
          [collapseOnHover]: !allChosen && isSomeChosen && !editedSectionId,
        })}
      >
        <Stack direction="column" gap="5" width="100%">
          <Divider width="100%" />
          <Typography size="b_md" color="grey_900">
            (<AppI18n k={'sections.ideal_candidate.more_section'} />)
          </Typography>
        </Stack>
      </Grid>
    </Stack>
  );
};
